'use client';

import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { VStack } from '@terra/ui-styled-system';
import { Add01Icon } from '@terra/ui-icons';
import type { KnowledgeSyncProviderType } from '@terra/shared-contracts-knowledge-sync';
import { Button } from '../../../components/button';
import { ContentCard } from '../../content-card/ContentCard';
import { KnowledgeSourceCard } from './KnowledgeSourceCard';
import type { KnowledgeSourceScope } from './knowledgeSourceScopes';

export type KnowledgeSourcesCardIntent = 'create' | 'settings';

export type KnowledgeSourcesCardSource = {
  provider: KnowledgeSyncProviderType;
  scopes: KnowledgeSourceScope[];
  indexedDocumentCount: number;
};

export type KnowledgeSourcesCardProps = {
  intent: KnowledgeSourcesCardIntent;
  sources: KnowledgeSourcesCardSource[];
  showEmptyState: boolean;
  onAdd: () => void;
  onEdit: (provider: KnowledgeSyncProviderType) => void;
  onRemove: (provider: KnowledgeSyncProviderType) => void;
  isReadOnly?: boolean;
  renderProviderLogo?: (provider: KnowledgeSyncProviderType) => ReactNode;
};

export const KnowledgeSourcesCard = ({
  intent,
  sources,
  showEmptyState,
  onAdd,
  onEdit,
  onRemove,
  isReadOnly = false,
  renderProviderLogo,
}: KnowledgeSourcesCardProps) => {
  const t = useTranslations('designSystem.documentsReports.knowledgeSources');
  const tCommon = useTranslations('designSystem.common');
  const isSettings = intent === 'settings';
  const hasSources = sources.length > 0;
  const hasCardHeader = !isSettings || !hasSources;

  return (
    <ContentCard
      textAlign={isSettings ? 'center' : 'left'}
      title={hasCardHeader ? t('title') : undefined}
      description={hasCardHeader ? t('description') : undefined}
      hasBackground={hasCardHeader}
      hasPadding={hasCardHeader}
      rightSlot={
        hasSources && hasCardHeader ? (
          <Button variant="outline" size="xs" onClick={onAdd}>
            <Add01Icon size={14} />
            {tCommon('add')}
          </Button>
        ) : null
      }
    >
      {showEmptyState ? (
        <Button
          variant="outline"
          size="sm"
          alignSelf={isSettings ? 'center' : 'flex-start'}
          fontWeight="normal"
          disabled={isReadOnly}
          onClick={isReadOnly ? undefined : onAdd}
        >
          {t('addKnowledgeSourcesIntegration')}
        </Button>
      ) : (
        <VStack alignItems="stretch" gap={3} w="full">
          {sources.map(({ provider, scopes, indexedDocumentCount }) => (
            <KnowledgeSourceCard
              key={provider}
              provider={provider}
              scopes={scopes}
              syncedAtLabel={t('syncedNow')}
              indexedDocumentCount={indexedDocumentCount}
              onEdit={onEdit}
              onRemove={onRemove}
              isReadOnly={isReadOnly}
              providerLogo={renderProviderLogo?.(provider)}
            />
          ))}
        </VStack>
      )}
    </ContentCard>
  );
};
